import { unref } from 'vue';

/**
 * Checks if HTML content contains images with public URLs
 * @param {string} html - The HTML content to check
 * @returns {boolean} - True if HTML contains images with public URLs
 */
export function hasImagesWithPublicUrls(html) {
  if (!html) return false;

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  const images = tempDiv.querySelectorAll('img');

  if (images.length === 0) return false;

  for (const img of images) {
    const src = img.getAttribute('src');
    if (src && (src.startsWith('http://') || src.startsWith('https://'))) {
      console.log('Found image with public URL:', src);
      return true;
    }
  }

  return false;
}

/**
 * Gets the public image URLs from the HTML content
 * @param {string} html - The HTML content to check
 * @returns {string[]} - An array of public image URLs
 */
export function getPublicImageUrls(html) {
  if (!html) return [];

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  const images = tempDiv.querySelectorAll('img');

  const publicImageUrls = [];

  for (const img of images) {
    const src = img.getAttribute('src');
    const alt = img.getAttribute('alt');
    if (src && (src.startsWith('http://') || src.startsWith('https://'))) {
      publicImageUrls.push({ src, alt });
    }
  }

  return publicImageUrls;
}

/**
 * Ensures that a new paragraph is added after an image if the image is the last node in the editor content.
 * This helps users easily switch to a new line after inserting an image at the end.
 *
 * @param {import('@tiptap/vue-2').Editor | null | undefined} editorInstance - The Tiptap editor instance.
 */
export function ensureParagraphAfterFinalImage(editorInstance) {
  const editor = unref(editorInstance);

  if (!editor || !editor.state || !editor.state.doc) {
    console.warn(
      'ensureParagraphAfterFinalImage: Editor instance or its document is not available.'
    );
    return;
  }

  const { doc } = editor.state;
  if (doc.content.size === 0) {
    // Document is empty
    return;
  }

  const lastNode = doc.lastChild;

  // Check if the document is not empty and the last node is an imageResize node
  if (lastNode && lastNode.type.name === 'imageResize') {
    editor.chain().focus().createParagraphNear().run();
  }
}

/**
 * Handles the paste of image files from the clipboard
 * @param {import('@tiptap/vue-2').Editor | null | undefined} editorInstance - The Tiptap editor instance.
 * @param {DataTransferItemList} items - The items to handle.
 * @returns {boolean} - True if any file was processed, especially images.
 */
export function handlePasteImageFileFromClipboard(editorInstance, items) {
  const editor = unref(editorInstance);

  if (!items) return false;

  let handled = false;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const file = item.getAsFile();
      if (file) {
        const reader = new FileReader();
        reader.onload = e => {
          const src = e.target.result;
          const alt = file.name;

          editor
            .chain()
            .focus()
            .insertContent({
              type: 'imageResize',
              attrs: { src, alt },
            })
            .command(({ tr, dispatch }) => {
              if (dispatch) {
                const paragraph = editor.schema.nodes.paragraph.create();
                tr.insert(tr.selection.to, paragraph);
                // Move cursor into the new paragraph
                tr.setSelection(
                  editor.state.selection.constructor.near(
                    tr.doc.resolve(tr.selection.to)
                  )
                );
              }
              return true;
            })
            .run();
        };
        reader.readAsDataURL(file);
      }
      handled = true; // Indicate that at least one image file was processed
    } else if (item.kind === 'file') {
      return true; // Do not handle other file types
    }
  }
  return handled; // Return true if any file was processed, especially images
}
